// src/Pages/Home.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";

/**
 * Home: Clean, quiet hero; single search pill; billboard Benefits.
 * SEO: Canonical + OG/Twitter tags + JSON-LD; crawlable toggle content.
 */
export default function Home() {
  const BRAND = "#7C9082";
  const BRAND_HOVER = "#6C7F72";
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenu, setMobileMenu] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [heroMode, setHeroMode] = useState("guest"); // "guest" | "host"
  const [showSearchSheet, setShowSearchSheet] = useState(false);

  // Search state
  const [where, setWhere] = useState("");
  const [start, setStart] = useState(""); // YYYY-MM-DD
  const [end, setEnd] = useState("");
  const [guests, setGuests] = useState(4);

  // Optional founding banner counter
  const [foundingLeft] = useState(147);

  // --- SEO helpers ----------------------------------------------------
  function upsertMeta(attr, key, content) {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  }
  function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  }
  function upsertJsonLd(id, json) {
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(json);
  }

  // Today for min=… on dates
  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }, []);

  // Respect URL hints & prefill search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");
    const bh = params.get("becomeHost");
    if (mode === "host" || bh === "1") setHeroMode("host");

    const w = params.get("where");
    const s = params.get("start");
    const e = params.get("end");
    const g = params.get("guests");
    if (w) setWhere(w);
    if (s) setStart(s);
    if (e) setEnd(e);
    if (g) {
      const num = parseInt(g, 10);
      setGuests(Number.isFinite(num) && num > 0 ? num : 1);
    }
  }, [location.search]);

  // --- SEO: Canonical + OG/Twitter + structured data -----------------
  useEffect(() => {
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${location.pathname}`; // strip query from canonical
    const siteName = "You’re Welcome";
    const brandTagline = "Book beautiful spaces by the day—or stay overnight";
    const descGuest =
      "Book beautiful homes in St. Louis for day use or overnight stays. Perfect for showers, offsites, photo shoots, and intimate dinners. No-Chores Checkout™ and one total price up front.";
    const descHost =
      "Earn more with simpler rules. Simple 3% host fee, cross-list friendly, two-way calendar sync, and fast payouts. Become a Founding Host in St. Louis.";
    const title =
      heroMode === "guest"
        ? `${siteName} — ${brandTagline}`
        : `${siteName} — Keep more of each booking`;

    // Baseline <title> and description
    document.title = title;
    upsertMeta("name", "description", heroMode === "guest" ? descGuest : descHost);

    // Canonical
    upsertLink("canonical", canonicalUrl);

    // OpenGraph
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", heroMode === "guest" ? descGuest : descHost);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:locale", "en_US");
    upsertMeta("property", "og:image", `${origin}/og-image.jpg`); // add this image in /public
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", heroMode === "guest" ? descGuest : descHost);
    upsertMeta("name", "twitter:image", `${origin}/og-image.jpg`);
    // Optional brand handle if you have it:
    // upsertMeta("name", "twitter:site", "@YourHandle");

    // JSON-LD: Organization + WebSite + FAQ (No-Chores Checkout™)
    upsertJsonLd("schema-org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": siteName,
      "url": origin,
      "logo": `${origin}/YoureWelcome Logo.png`,
    });
    upsertJsonLd("schema-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": siteName,
      "url": origin,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${origin}/explore?where={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
    upsertJsonLd("schema-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is No-Chores Checkout™?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Leave it tidy. No laundry, no trash runs, no stripping beds—ever. Hosts handle turnover after you depart.",
          },
        },
        {
          "@type": "Question",
          "name": "Do prices include fees?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Yes. You see one total up front—plus tax only. No hidden platform fees at checkout.",
          },
        },
      ],
    });
  }, [heroMode, location.pathname]);

  // --- UI actions -----------------------------------------------------
  function openStart() {
    setShowStartModal(true);
  }
  function closeStart() {
    setShowStartModal(false);
  }
  function startAsGuest() {
    if (!user) navigate("/account?tab=signup");
    else navigate("/explore");
  }
  function startAsHost() {
    if (!user) navigate("/account?tab=signup&becomeHost=1");
    else navigate("/host/listings/new");
  }
  function submitSearch(e) {
    if (e) e.preventDefault();
    const safeGuests = Number.isFinite(guests) && guests > 0 ? guests : 1;
    let s = start;
    let eDate = end;
    if (s && eDate && eDate < s) eDate = s;

    const params = new URLSearchParams();
    if (where) params.set("where", where);
    if (s) params.set("start", s);
    if (eDate) params.set("end", eDate);
    params.set("guests", String(safeGuests));
    navigate(`/explore?${params.toString()}`);
    setShowSearchSheet(false);
  }

  const collections = [
    { label: "Baby showers", q: "baby-shower" },
    { label: "Team offsites", q: "offsite" },
    { label: "Photo shoots", q: "photoshoot" },
    { label: "Elopements", q: "elopement" },
    { label: "Overnights", q: "overnight" },
    { label: "Great for 12–20", q: "capacity:12-20" },
  ];

  // Lock scroll when the search sheet is open
  useEffect(() => {
    if (showSearchSheet) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => (document.body.style.overflow = prev);
    }
  }, [showSearchSheet]);

  return (
    <div
      className="min-h-screen bg-white text-neutral-900 selection:text-white"
      style={{ "--brand": BRAND, "--brandHover": BRAND_HOVER }}
    >
      {/* STICKY FOUNDING BANNER */}
      <div className="sticky top-0 z-50 w-full bg-[var(--brand)] px-3 py-2 text-center text-white md:px-6">
        <span className="text-sm font-semibold">Founding 200</span>
        <span className="mx-2 text-sm">• Free pro photos + 0% host fee on first bookings</span>
        <span className="text-sm opacity-90">{foundingLeft} spots left</span>
        <button
          onClick={startAsHost}
          className="ml-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-semibold hover:bg-white/20"
          aria-label="Claim Founding Host perks"
        >
          Claim perks
        </button>
      </div>

      {/* NAVBAR */}
      <header className="sticky top-[40px] z-40 w-full border-b border-neutral-200 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-3" aria-label="You’re Welcome Home">
            <picture>
              <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
              <img src="/YoureWelcome Logo.png" alt="You’re Welcome" className="h-8 w-auto md:h-10" />
            </picture>
          </Link>

          {/* Desktop nav */}
          <div className="hidden items-center gap-8 md:flex">
            <Link to="/explore" className="text-sm text-neutral-700 hover:text-[var(--brand)]">Browse</Link>
            <a href="#benefits" className="text-sm text-neutral-700 hover:text-[var(--brand)]">Benefits</a>
            <a href="#hosts" className="text-sm text-neutral-700 hover:text-[var(--brand)]">For hosts</a>

            {user && (
              <>
                <Link to="/trips" className="text-sm text-neutral-700 hover:text-[var(--brand)]" title="View your bookings">My bookings</Link>
                <Link to="/messages" className="text-sm text-neutral-700 hover:text-[var(--brand)]" title="Messages">Messages</Link>
              </>
            )}

            {!user ? (
              <>
                <Link to="/account" className="rounded-full px-3 py-1.5 text-sm font-medium text-neutral-800 hover:bg-neutral-100">Sign in</Link>
                <button
                  onClick={openStart}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white transition"
                  style={{ backgroundColor: "var(--brand)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--brandHover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--brand)")}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                <Link to="/account" className="rounded-full px-3 py-1.5 text-sm font-medium text-neutral-800 hover:bg-neutral-100">Account</Link>
                <button
                  onClick={openStart}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white transition"
                  style={{ backgroundColor: "var(--brand)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--brandHover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--brand)")}
                >
                  Get started
                </button>
              </>
            )}

            <button
              onClick={startAsHost}
              className="rounded-full border px-4 py-2 text-sm font-semibold transition"
              style={{ borderColor: "var(--brand)" }}
            >
              List your space
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="inline-flex items-center justify-center rounded-md p-2 md:hidden"
            onClick={() => setMobileMenu((v) => !v)}
            aria-label="Toggle menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
              className="h-6 w-6 text-neutral-900">
              <path d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenu && (
          <div className="border-t border-neutral-200 md:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3">
              <Link to="/explore" className="py-2 text-sm" onClick={() => setMobileMenu(false)}>Browse</Link>
              <a href="#benefits" className="py-2 text-sm" onClick={() => setMobileMenu(false)}>Benefits</a>
              <a href="#hosts" className="py-2 text-sm" onClick={() => setMobileMenu(false)}>For hosts</a>

              <div className="mt-2 grid grid-cols-2 gap-2">
                {user && (
                  <>
                    <Link to="/trips" className="rounded-full border border-neutral-300 px-4 py-2 text-sm text-center" onClick={() => setMobileMenu(false)}>My bookings</Link>
                    <Link to="/messages" className="rounded-full border border-neutral-300 px-4 py-2 text-sm text-center" onClick={() => setMobileMenu(false)}>Messages</Link>
                  </>
                )}
                {!user ? (
                  <>
                    <Link to="/account" className="rounded-full border border-neutral-300 px-4 py-2 text-sm text-center" onClick={() => setMobileMenu(false)}>Sign in</Link>
                    <button
                      className="rounded-full px-4 py-2 text-sm font-semibold text-white"
                      style={{ backgroundColor: "var(--brand)" }}
                      onClick={() => { setMobileMenu(false); openStart(); }}
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/account" className="rounded-full border border-neutral-300 px-4 py-2 text-sm text-center" onClick={() => setMobileMenu(false)}>Account</Link>
                    <button
                      className="rounded-full px-4 py-2 text-sm font-semibold text-white"
                      style={{ backgroundColor: "var(--brand)" }}
                      onClick={() => { setMobileMenu(false); openStart(); }}
                    >
                      Get started
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setMobileMenu(false); startAsHost(); }}
                  className="col-span-2 rounded-full border px-4 py-2 text-sm font-semibold"
                  style={{ borderColor: "var(--brand)" }}
                >
                  List your space
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* HERO — minimal */}
      <section id="home" className="relative overflow-hidden selection:bg-[var(--brand)]">
        <div className="mx-auto max-w-7xl px-4 py-12 md:py-16">
          <div className="mx-auto mb-6 flex max-w-3xl items-center justify-center gap-2">
            <TogglePill label="I'm a guest" active={heroMode === "guest"} onClick={() => setHeroMode("guest")} />
            <TogglePill label="I'm a host" active={heroMode === "host"} onClick={() => setHeroMode("host")} />
          </div>

          <div className="mx-auto max-w-3xl text-center">
            {heroMode === "guest" ? (
              <>
                <h1 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl">
                  Book beautiful spaces by the day—or stay overnight
                </h1>
                <p className="mt-3 text-pretty text-neutral-600 sm:text-lg">
                  Showers, offsites, photo shoots, intimate dinners. Optional overnights at select homes.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl">
                  Keep more of each booking—day or night
                </h1>
                <p className="mt-3 text-pretty text-neutral-600 sm:text-lg">
                  Simple 3% host fee. Cross-list friendly. Free pro photos for the first 200.
                </p>
              </>
            )}

            {/* One search pill (opens sheet) */}
            <button
              type="button"
              onClick={() => setShowSearchSheet(true)}
              className="mt-6 flex w-full items-center justify-between rounded-full border border-neutral-200 bg-white px-4 py-3 text-left text-sm shadow-sm sm:mx-auto sm:w-[680px]"
              aria-label="Open search"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-white" style={{ backgroundColor: "var(--brand)" }}>
                  <SearchIcon />
                </div>
                <div className="min-w-0">
                  <div className="truncate font-semibold">{where || "Where to?"}</div>
                  <div className="truncate text-xs text-neutral-500">
                    {start || end ? `${start || "Add dates"} → ${end || "Add dates"}` : "Add dates"} · {guests} guest{guests > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 text-neutral-400">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>

            <div className="mt-4 inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-medium" style={{ borderColor: "var(--brand)", color: "var(--brand)" }}>
              Launching in St. Louis
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="border-t border-neutral-200 bg-white scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <h2 className="mx-auto max-w-4xl text-center text-3xl font-semibold leading-tight sm:text-4xl md:text-5xl">
            {heroMode === "guest" ? "Big, simple rules that put people first." : "Earn more with simpler rules and less hassle."}
          </h2>
          <div className="mx-auto mt-4 h-1 w-16 rounded-full" style={{ backgroundColor: "var(--brand)" }} />

          {heroMode === "guest" ? (
            <>
              <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2">
                <BigFeature brand={BRAND} title="No-Chores Checkout™" text="Leave it tidy. No laundry, no trash runs, no stripping beds—ever." />
                <BigFeature brand={BRAND} title="One Price. No Surprises." text="See one total up front—plus tax only. No hidden platform fees." />
              </div>

              <div className="mx-auto mt-6 grid max-w-6xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Bullet title="Instant, keyless entry" />
                <Bullet title="Clean Guarantee" />
                <Bullet title="Work-ready badges" />
                <Bullet title="Flexible day-use" />
              </div>

              <p className="mx-auto mt-6 max-w-3xl text-center text-xs text-neutral-500">
                “™” indicates our claim to the term; it isn’t a registered mark.
              </p>

              <div className="mt-8 flex items-center justify-center">
                <Link
                  to="/explore"
                  className="rounded-full px-6 py-3 text-sm font-semibold text-white transition"
                  style={{ backgroundColor: BRAND }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_HOVER)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND)}
                >
                  Explore spaces
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <BigFeature brand={BRAND} title="3% host fee only" text="Keep more of each payout—no junk add-ons." />
                <BigFeature brand={BRAND} title="Day+Night yield" text="Offer daytime bookings and overnights." />
                <BigFeature brand={BRAND} title="Auto Rules" text="Quiet hours, capacity, and badges enforced." />
                <BigFeature brand={BRAND} title="Calendar sync" text="Airbnb · Vrbo · iCal/Google—two-way." />
                <BigFeature brand={BRAND} title="Fast payouts" text="Standard after check-in; instant available." />
                <BigFeature brand={BRAND} title="Cross-list OK" text="Bring your listings. No exclusivity." />
              </div>

              <div className="mt-10 flex items-center justify-center">
                <button
                  onClick={startAsHost}
                  className="rounded-full px-7 py-3 text-sm font-semibold text-white transition"
                  style={{ backgroundColor: BRAND }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_HOVER)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND)}
                >
                  Become a host
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* COLLECTIONS */}
      <section id="browse">
        <div className="mx-auto max-w-7xl px-4 pb-4 md:px-6">
          <div className="mb-3 flex items-end justify-between">
            <div>
              <h3 className="text-xl font-semibold sm:text-2xl">Explore collections</h3>
              <div className="mt-2 h-0.5 w-10 rounded-full" style={{ backgroundColor: "var(--brand)" }} />
            </div>
            <Link
              to="/explore"
              className="hidden rounded-full px-4 py-2 text-sm font-semibold text-white transition sm:inline-flex"
              style={{ backgroundColor: "var(--brand)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--brandHover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--brand)")}
            >
              Browse all
            </Link>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {collections.map((c) => (
              <CollectionCard key={c.q} label={c.label} q={c.q} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-neutral-200 bg-neutral-50 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="mx-auto mb-8 max-w-2xl text-center">
            <h2 className="text-2xl font-semibold sm:text-3xl">How it works</h2>
            <div className="mx-auto mt-2 h-0.5 w-12 rounded-full" style={{ backgroundColor: "var(--brand)" }} />
            <p className="mt-3 text-neutral-600">Three steps. Under five minutes.</p>
          </div>

          <div className="grid gap-6 sm:grid-cols-3">
            <Step num={1} title="Pick your dates" text="Book a single day or choose multiple nights—host rules apply." brand={BRAND} />
            <Step num={2} title="Select a space" text="Curated homes with clear quiet hours, capacity, and amenities." brand={BRAND} />
            <Step num={3} title="Pay & message" text="Secure checkout, then coordinate details with the host." brand={BRAND} />
          </div>
        </div>
      </section>

      {/* HOST PITCH */}
      <section id="hosts" className="scroll-mt-24">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="grid items-center gap-6 lg:grid-cols-2">
            <div>
              <h3 className="text-2xl font-semibold sm:text-3xl">Become a Founding Host</h3>
              <div className="mt-2 h-0.5 w-12 rounded-full" style={{ backgroundColor: "var(--brand)" }} />
              <p className="mt-3 text-neutral-600">
                Keep more of each payout with a simple 3% fee. Cross-list friendly, two-way calendar sync, and concierge onboarding.
                First 200 hosts get free pro photos and 0% host fee on their first bookings.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Badge>Free pro photos</Badge>
                <Badge>0% host fee (intro)</Badge>
                <Badge>Priority placement</Badge>
                <Badge>Founding badge</Badge>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={startAsHost}
                  className="rounded-full px-6 py-3 text-sm font-semibold text-white transition"
                  style={{ backgroundColor: "var(--brand)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--brandHover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--brand)")}
                >
                  List your space
                </button>
                <a
                  href="#benefits"
                  className="rounded-full border px-6 py-3 text-sm font-semibold transition"
                  style={{ borderColor: "var(--brand)" }}
                >
                  See all perks
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <MiniFact label="Cross-list OK" value="Yes" />
                <MiniFact label="Calendar sync" value="Airbnb · Vrbo · Google" />
                <MiniFact label="Setup help" value="Concierge" />
                <MiniFact label="Payouts" value="Fast / Instant*" />
              </div>
              <p className="mt-3 text-xs text-neutral-500">*Instant payouts subject to bank & Stripe availability and small bank fee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <picture>
                <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
                <img src="/YoureWelcome Logo.png" alt="You’re Welcome" className="h-6 w-auto" loading="lazy" />
              </picture>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-600">
              <a href="#benefits" className="hover:text-[var(--brand)]">Benefits</a>
              <Link to="/explore" className="hover:text-[var(--brand)]">Browse</Link>
              <a href="#hosts" className="hover:text-[var(--brand)]">For hosts</a>
              <button
                onClick={startAsHost}
                className="rounded-full px-4 py-2 text-white transition"
                style={{ backgroundColor: "var(--brand)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--brandHover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--brand)")}
              >
                List your space
              </button>
            </div>
          </div>
          <p className="mt-6 text-xs text-neutral-500">© {new Date().getFullYear()} You’re Welcome. All rights reserved.</p>
        </div>
      </footer>

      {/* START MODAL */}
      {showStartModal && (
        <StartModal
          isAuthed={!!user}
          onClose={closeStart}
          onGuest={startAsGuest}
          onHost={startAsHost}
          brand={BRAND}
        />
      )}

      {/* SEARCH SHEET */}
      {showSearchSheet && (
        <SearchSheet
          where={where}
          setWhere={setWhere}
          start={start}
          setStart={setStart}
          end={end}
          setEnd={setEnd}
          guests={guests}
          setGuests={setGuests}
          todayStr={todayStr}
          onClose={() => setShowSearchSheet(false)}
          onSubmit={submitSearch}
          brand={BRAND}
        />
      )}
    </div>
  );
}

/* --------------------------- Small UI Bits --------------------------- */

function TogglePill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full px-4 py-2 text-sm font-semibold ${active ? "text-white" : "text-neutral-700"}`}
      style={{
        backgroundColor: active ? "var(--brand)" : "transparent",
        border: active ? "1px solid var(--brand)" : "1px solid #e5e5e5",
      }}
    >
      {label}
    </button>
  );
}

function GuestStepper({ value, onChange }) {
  const dec = () => onChange(Math.max(1, (parseInt(value, 10) || 1) - 1));
  const inc = () => onChange(Math.min(50, (parseInt(value, 10) || 1) + 1));
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={dec} className="h-7 w-7 rounded-full border border-neutral-300 text-sm">−</button>
      <input
        id="guests"
        type="number"
        min={1}
        max={50}
        className="w-12 bg-transparent text-center text-sm outline-none"
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          onChange(Number.isFinite(v) && v > 0 ? v : 1);
        }}
        inputMode="numeric"
        aria-label="Guests"
      />
      <button type="button" onClick={inc} className="h-7 w-7 rounded-full border border-neutral-300 text-sm">+</button>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
      <circle cx="11" cy="11" r="7"></circle>
      <path d="M20 20l-3.5-3.5"></path>
    </svg>
  );
}

function Step({ num, title, text, brand }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6">
      <div
        className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white"
        style={{ backgroundColor: brand }}
      >
        {num}
      </div>
      <h4 className="text-base font-semibold">{title}</h4>
      <p className="mt-1 text-sm text-neutral-600">{text}</p>
    </div>
  );
}

function CollectionCard({ label, q }) {
  return (
    <Link
      to={`/explore?collection=${encodeURIComponent(q)}`}
      className="group rounded-2xl border border-neutral-200 bg-white p-5 transition hover:shadow-[0_8px_30px_-18px_rgba(0,0,0,0.35)]"
    >
      <div className="text-base font-semibold">{label}</div>
      <div className="mt-1 text-sm text-neutral-600">Curated homes · Book in minutes</div>
      <div
        className="mt-4 h-10 w-10 rounded-xl opacity-0 transition group-hover:opacity-100"
        style={{ backgroundColor: "var(--brand)" }}
      />
    </Link>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700">
      {children}
    </span>
  );
}

function MiniFact({ label, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="mt-1 text-base font-semibold">{value}</div>
    </div>
  );
}

/* --------------------------- Benefits Bits --------------------------- */

function BigFeature({ brand, title, text }) {
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
      <div
        className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-2xl"
        style={{ backgroundColor: "rgba(124,144,130,0.12)" }}
      >
        <IconStar />
      </div>
      <div className="text-2xl font-semibold sm:text-3xl">{title}</div>
      {text && <div className="mt-2 text-base leading-relaxed text-neutral-600 sm:text-lg">{text}</div>}
    </div>
  );
}

function Bullet({ title }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-5">
      <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full">
        <IconCheck />
      </span>
      <div className="text-base font-medium sm:text-lg">{title}</div>
    </div>
  );
}

function IconCheck() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );
}

/* --------------------------- Start Modal --------------------------- */

function StartModal({ isAuthed, onClose, onGuest, onHost, brand }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose how you want to use You’re Welcome"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold">How do you want to get started?</h3>
        <p className="mt-1 text-sm text-neutral-600">You can always do both later.</p>

        <div className="mt-4 grid gap-3">
          <button
            onClick={onGuest}
            className="w-full rounded-xl border px-4 py-3 text-left text-sm font-semibold transition"
            style={{ borderColor: brand }}
          >
            {isAuthed ? "Book spaces now" : "Sign up to book spaces"}
          </button>
          <button
            onClick={onHost}
            className="w-full rounded-xl border px-4 py-3 text-left text-sm font-semibold transition"
            style={{ borderColor: brand }}
          >
            {isAuthed ? "List my space" : "Sign up to list my space"}
          </button>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-3 py-1.5 text-sm hover:bg-neutral-100">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Search Sheet --------------------------- */

function SearchSheet({
  where,
  setWhere,
  start,
  setStart,
  end,
  setEnd,
  guests,
  setGuests,
  todayStr,
  onClose,
  onSubmit,
  brand,
}) {
  const firstFieldRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Search homes"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-x-0 bottom-0 top-[10vh] rounded-t-3xl bg-white shadow-xl">
        <div className="mx-auto max-w-2xl px-4 pb-[calc(env(safe-area-inset-bottom,0)+88px)] pt-4">
          {/* Drag handle */}
          <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-neutral-200" />

          <h3 className="mb-2 text-center text-base font-semibold">Search</h3>

          <form className="grid gap-3" onSubmit={onSubmit}>
            {/* WHERE */}
            <div className="rounded-2xl border border-neutral-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Where</div>
              <input
                ref={firstFieldRef}
                id="sheet-where"
                list="where-suggestions-mobile"
                className="mt-1 w-full bg-transparent text-base outline-none placeholder:text-neutral-400"
                placeholder="Neighborhood or city"
                value={where}
                onChange={(e) => setWhere(e.target.value)}
                autoComplete="off"
                inputMode="text"
              />
              <datalist id="where-suggestions-mobile">
                <option value="Central West End" />
                <option value="Lafayette Square" />
                <option value="Soulard" />
                <option value="Tower Grove" />
                <option value="Clayton" />
                <option value="Maplewood" />
              </datalist>
            </div>

            {/* DATES */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-neutral-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Check in</div>
                <input
                  id="sheet-start"
                  type="date"
                  className="mt-1 w-full bg-transparent text-base outline-none"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  min={todayStr}
                />
              </div>
              <div className="rounded-2xl border border-neutral-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Check out</div>
                <input
                  id="sheet-end"
                  type="date"
                  className="mt-1 w-full bg-transparent text-base outline-none"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  min={start || todayStr}
                />
              </div>
            </div>

            {/* WHO */}
            <div className="rounded-2xl border border-neutral-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Who</div>
                  <div className="mt-1 text-base font-semibold">
                    {guests} guest{guests > 1 ? "s" : ""}
                  </div>
                </div>
                <GuestStepper value={guests} onChange={setGuests} />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-1 flex items-center justify-between">
              <button type="button" onClick={onClose} className="rounded-full px-4 py-3 text-sm font-semibold hover:bg-neutral-100">
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white"
                style={{ backgroundColor: brand }}
              >
                <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                  <SearchIcon />
                </span>
                Search
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
